import { useState, useEffect } from 'react';
import { normalizeStatus } from '../utils/configItemLifecycle';
import { ArrowLeft, Plus, Layers, CheckCircle2, Clock, AlertTriangle, Settings, FileText, Sparkles, Loader2, Users, Pencil, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { ClientConfigItemsTable } from './ClientConfigItemsTable';
import { ConsultantReviewPanel } from './ConsultantReviewPanel';
import type { HierarchicalConfigItem } from '../types/phase1e';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';
import { SpaceEditDialog } from './SpaceEditDialog';
import { SpaceReadinessSummary, SpaceTeamsPanel } from './SpaceTeamsPanel';
import { itemsInSpace, jiraSpaceHref, spaceTypeLabel, teamDetailsOf } from '../utils/spaceTeams';
import { loadSpacesOverview, type SpacesOverview } from '../utils/spaceTeamsApi';

interface SpaceDetailViewProps {
  spaceId: string; // Now we receive spaceId from URL params
  companyId: string;
  accessToken: string;
  currentUser: any;
  onBack: () => void;
}

export function SpaceDetailView({
  spaceId,
  companyId,
  accessToken,
  currentUser,
  onBack,
}: SpaceDetailViewProps) {
  const [space, setSpace] = useState<any>(null);
  const [configItems, setConfigItems] = useState<HierarchicalConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<any[]>([]);
  const [showSuggestionsDialog, setShowSuggestionsDialog] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set());
  const [addingSuggestions, setAddingSuggestions] = useState(false);
  const [activeTab, setActiveTab] = useState('items');
  /** RESTORE_PLAN R1.5: the company's other spaces (edit duplicate check), readiness and request types */
  const [companySpaces, setCompanySpaces] = useState<any[]>([]);
  const [overview, setOverview] = useState<SpacesOverview | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    loadSpace();
    loadSpaceConfigItems();
    loadOverview();
  }, [spaceId]);

  const loadOverview = async () => {
    try {
      setOverview(await loadSpacesOverview(companyId, accessToken));
    } catch (error) {
      console.warn('Could not load the spaces overview:', error);
    }
  };

  const handleSpaceChanged = async (saved?: Record<string, unknown>) => {
    if (saved && saved.id === spaceId) setSpace((prev: any) => ({ ...prev, ...saved }));
    else await loadSpace();
    await loadOverview();
  };

  const loadSpace = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/spaces/all`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const foundSpace = (data.spaces || []).find((s: any) => s.id === spaceId);
        setCompanySpaces((data.spaces || []).filter((s: any) => s.companyId === companyId));
        if (foundSpace) {
          setSpace(foundSpace);
        } else {
          toast.error('Space not found');
        }
      }
    } catch (error) {
      console.error('Error loading space:', error);
      toast.error('Failed to load space');
    }
  };

  const loadSpaceConfigItems = async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${accessToken}` };
      const base = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;
      // The per-item store is what the client page's space card counts; the company's older list
      // only adds items it alone holds for this space.
      const [perItemRes, olderRes] = await Promise.all([
        fetch(`${base}/config-items/${encodeURIComponent(companyId)}`, { headers }),
        fetch(`${base}/companies/${companyId}/config-items`, { headers }),
      ]);
      if (!perItemRes.ok && !olderRes.ok) throw new Error(`Config items failed (${perItemRes.status})`);
      const perItem = perItemRes.ok ? (await perItemRes.json()).items || [] : [];
      const older = olderRes.ok ? (await olderRes.json()).items || [] : [];
      setConfigItems(itemsInSpace(spaceId, perItem, older));
    } catch (error) {
      console.error('Error loading space config items:', error);
      toast.error('Failed to load config items');
    } finally {
      setLoading(false);
    }
  };

  const handleAISuggestCIs = async () => {
    try {
      setAiGenerating(true);
      toast.info('AI is analyzing SOW and template...');
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/ai/suggest-space-cis`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            companyId,
            spaceId,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(`AI suggested ${data.suggestions?.length || 0} configuration items`);
        setAiSuggestions(data.suggestions || []);
        setShowSuggestionsDialog(true);
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to generate AI suggestions');
      }
    } catch (error) {
      console.error('Error generating AI suggestions:', error);
      toast.error('Failed to generate AI suggestions');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAddSelectedSuggestions = async () => {
    try {
      setAddingSuggestions(true);
      toast.info('Adding selected suggestions...');
      
      // Filter to only the selected suggestions
      const selectedIds = Array.from(selectedSuggestions);
      const suggestionsToAdd = aiSuggestions.filter(s => selectedIds.includes(s.id));
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/add-space-cis`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            companyId,
            spaceId,
            suggestions: suggestionsToAdd,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(`Added ${data.count || 0} configuration items`);
        // Reload config items to show the new suggestions
        await loadSpaceConfigItems();
        setShowSuggestionsDialog(false);
        setSelectedSuggestions(new Set());
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to add suggestions');
      }
    } catch (error) {
      console.error('Error adding suggestions:', error);
      toast.error('Failed to add suggestions');
    } finally {
      setAddingSuggestions(false);
    }
  };

  // Helper to format space type for display
  const formatSpaceType = (type: string) => spaceTypeLabel(type);

  // Calculate space metrics
  const totalItems = configItems.length;
  const completedItems = configItems.filter(item => normalizeStatus(item) === 'approved').length;
  const inProgressItems = configItems.filter(item => normalizeStatus(item) === 'discovery').length;
  const waitingConsultant = configItems.filter(item => item.ballInCourt === 'consultant').length;
  const waitingClient = configItems.filter(item => item.ballInCourt === 'client').length;
  const completionPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  // Get unique categories
  const categories = Array.from(new Set(configItems.map(item => item.category).filter(Boolean)));
  const categoryCounts = categories.reduce((acc, category) => {
    acc[category] = configItems.filter(item => item.category === category).length;
    return acc;
  }, {} as Record<string, number>);

  if (loading || !space) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="size-4 mr-2" />
          Back to Spaces
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
            <p className="text-muted-foreground">Loading space...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div>
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="size-4 mr-2" />
          Back to Spaces
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Layers className="size-6 text-blue-600" />
                  <div>
                    <CardTitle className="text-2xl">{space?.name}</CardTitle>
                    <CardDescription className="mt-1">
                      {formatSpaceType(space?.type || '')} • {space?.companyName}
                    </CardDescription>
                  </div>
                </div>
                {space?.description && (
                  <p className="text-sm text-muted-foreground mt-2">{space.description}</p>
                )}
                {jiraSpaceHref(space?.jiraSpaceUrl) && (
                  <a
                    href={jiraSpaceHref(space?.jiraSpaceUrl) as string}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline mt-2"
                  >
                    <ExternalLink className="size-3.5" aria-hidden />
                    Open in Jira
                  </a>
                )}
              </div>
              <Badge variant={completionPercentage === 100 ? 'default' : 'secondary'} className="text-sm">
                {completionPercentage}% Complete
              </Badge>
            </div>
          </CardHeader>

          <CardContent>
            {/* Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <FileText className="size-4" />
                  <span className="text-xs font-medium">Total Items</span>
                </div>
                <p className="text-2xl font-bold">{totalItems}</p>
              </div>

              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <CheckCircle2 className="size-4" />
                  <span className="text-xs font-medium">Completed</span>
                </div>
                <p className="text-2xl font-bold">{completedItems}</p>
              </div>

              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <Clock className="size-4" />
                  <span className="text-xs font-medium">In Progress</span>
                </div>
                <p className="text-2xl font-bold">{inProgressItems}</p>
              </div>

              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 text-orange-600 mb-1">
                  <AlertTriangle className="size-4" />
                  <span className="text-xs font-medium">Needs Attention</span>
                </div>
                <p className="text-2xl font-bold">{waitingConsultant}</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-muted-foreground">Overall Progress</span>
                <span className="font-medium">{completedItems} of {totalItems} items</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
            </div>

            {/* Teams, workflows, work types and readiness (RESTORE_PLAN R1.5) */}
            {overview?.readiness[spaceId] && (
              <div className="mt-4">
                <SpaceReadinessSummary readiness={overview.readiness[spaceId]} />
              </div>
            )}

            {/* Categories Summary */}
            {categories.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium mb-2">Categories in this Space</h4>
                <div className="flex flex-wrap gap-2">
                  {categories.map(category => (
                    <Badge key={category} variant="outline" className="capitalize">
                      {formatCategoryLabel(category)} ({categoryCounts[category]})
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="items">
            <FileText className="size-4 mr-2" />
            Configuration Items ({totalItems})
          </TabsTrigger>
          <TabsTrigger value="teams">
            <Users className="size-4 mr-2" />
            Teams ({teamDetailsOf(space).length})
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="size-4 mr-2" />
            Space Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="mt-6">
          {loading ? (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
                <p className="text-muted-foreground">Loading configuration items...</p>
              </CardContent>
            </Card>
          ) : totalItems === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="size-12 mx-auto mb-3 text-gray-400" />
                <p className="font-medium mb-2">No configuration items yet</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Add configuration items to this space to get started
                </p>
                <div className="flex items-center justify-center gap-3">
                  <Button>
                    <Plus className="size-4 mr-2" />
                    Add Configuration Item
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleAISuggestCIs}
                    disabled={aiGenerating}
                    className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200 hover:border-purple-300"
                  >
                    {aiGenerating ? (
                      <Loader2 className="size-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="size-4 mr-2 text-purple-600" />
                    )}
                    AI Suggest CIs from SOW
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* AI Suggestion Banner */}
              <Card className="mb-4 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <Sparkles className="size-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-purple-900">
                          Need more configuration items?
                        </p>
                        <p className="text-sm text-purple-700">
                          AI can analyze your SOW and suggest additional CIs based on the Atlassian template
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleAISuggestCIs}
                      disabled={aiGenerating}
                      className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white flex-shrink-0 w-full sm:w-auto"
                    >
                      {aiGenerating ? (
                        <>
                          <Loader2 className="size-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="size-4 mr-2" />
                          AI Suggest CIs
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <ClientConfigItemsTable
                items={configItems}
                spaceId={spaceId}
                companyId={companyId}
                companyName={space?.companyName}
                accessToken={accessToken}
                currentUser={currentUser}
                onUpdate={() => {
                  loadSpaceConfigItems();
                }}
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="teams" className="mt-6">
          <Card>
            <CardContent className="pt-6">
              <SpaceTeamsPanel
                space={space}
                requestTypes={overview?.requestTypes || []}
                accessToken={accessToken}
                onChanged={handleSpaceChanged}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle>Space Settings</CardTitle>
                  <CardDescription>Configure this space's properties and behavior</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil className="size-4 mr-2" />
                  Edit Space
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Space Name</label>
                  <p className="text-sm text-muted-foreground mt-1">{space?.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Space Type</label>
                  <p className="text-sm text-muted-foreground mt-1">{formatSpaceType(space?.type || 'Service Project')}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {space?.description || 'No description provided'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Space Key</label>
                  <p className="text-sm text-muted-foreground mt-1">{space?.key || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Jira Space URL</label>
                  <p className="text-sm text-muted-foreground mt-1 break-all">{space?.jiraSpaceUrl || 'Not set'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <SpaceEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        space={space}
        otherSpaces={companySpaces}
        accessToken={accessToken}
        onSaved={handleSpaceChanged}
      />

      {/* AI Suggestions Dialog */}
      <Dialog open={showSuggestionsDialog} onOpenChange={setShowSuggestionsDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="size-5 text-purple-600" />
              AI Suggested Configuration Items
            </DialogTitle>
            <DialogDescription>
              Review and select the items you want to add to this space. AI analyzed your SOW and the Atlassian template to generate these suggestions.
            </DialogDescription>
          </DialogHeader>
          
          {/* Select All */}
          <div className="flex items-center justify-between border-b pb-3">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedSuggestions.size === aiSuggestions.length && aiSuggestions.length > 0}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setSelectedSuggestions(new Set(aiSuggestions.map(s => s.id)));
                  } else {
                    setSelectedSuggestions(new Set());
                  }
                }}
              />
              <span className="text-sm font-medium">
                {selectedSuggestions.size === aiSuggestions.length && aiSuggestions.length > 0
                  ? 'Deselect All'
                  : 'Select All'}
              </span>
            </div>
            <span className="text-sm text-muted-foreground">
              {selectedSuggestions.size} of {aiSuggestions.length} selected
            </span>
          </div>

          {/* Scrollable Suggestions List */}
          <div className="max-h-[50vh] overflow-y-auto space-y-3 border rounded-lg p-4">
            {aiSuggestions.map((suggestion: any) => (
              <div
                key={suggestion.id}
                className={`border rounded-lg p-3 hover:bg-gray-50 transition-colors ${
                  selectedSuggestions.has(suggestion.id) ? 'bg-blue-50 border-blue-300' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={selectedSuggestions.has(suggestion.id)}
                    onCheckedChange={(checked) => {
                      const newSelected = new Set(selectedSuggestions);
                      if (checked) {
                        newSelected.add(suggestion.id);
                      } else {
                        newSelected.delete(suggestion.id);
                      }
                      setSelectedSuggestions(newSelected);
                    }}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <h4 className="font-medium mb-1">{suggestion.name}</h4>
                    {suggestion.description && (
                      <p className="text-sm text-muted-foreground mb-2">
                        {suggestion.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      {suggestion.category && (
                        <Badge variant="outline" className="capitalize text-xs">
                          {formatCategoryLabel(suggestion.category)}
                        </Badge>
                      )}
                      {suggestion.priority && (
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            suggestion.priority === 'high' ? 'bg-red-50 text-red-700 border-red-200' :
                            suggestion.priority === 'medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                            'bg-gray-50 text-gray-700 border-gray-200'
                          }`}
                        >
                          {suggestion.priority}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-xs">
                        <Sparkles className="size-3 mr-1" />
                        AI Generated
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Actions - Single Button */}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowSuggestionsDialog(false);
                setSelectedSuggestions(new Set());
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSelectedSuggestions}
              disabled={selectedSuggestions.size === 0 || addingSuggestions}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            >
              {addingSuggestions ? (
                <>
                  <Loader2 className="size-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="size-4 mr-2" />
                  Add {selectedSuggestions.size} Selected
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}